import { Button, Dialog, DialogContent, FormControlLabel, IconButton, MenuItem, Switch, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { CategorySearchSelect } from "../../components/CategorySearchSelect";
import { DatePickerField } from "../../components/DatePickerField";
import { AccountSelect, UserSelect } from "../../components/FinanceSelects";
import { financeApi } from "../../lib/api";
import type { Account, Category, Group, Member, TransactionType } from "../../lib/types";

type ExportFormat = "xls" | "csv";

export function ExportRecordsModal({
  group,
  accounts,
  categories,
  members,
  onClose,
}: {
  group: Group;
  accounts: Account[];
  categories: Category[];
  members: Member[];
  onClose: () => void;
}) {
  const [format, setFormat] = useState<ExportFormat>("xls");
  const [includeRecords, setIncludeRecords] = useState(true);
  const [includeCategories, setIncludeCategories] = useState(true);
  const [includeAccounts, setIncludeAccounts] = useState(true);
  const [includeCurrencies, setIncludeCurrencies] = useState(true);
  const [type, setType] = useState<TransactionType | "">("");
  const [categoryId, setCategoryId] = useState("");
  const [userId, setUserId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [error, setError] = useState("");

  const availableCategories = useMemo(
    () => categories.filter((category) => !category.is_archived && (!type || category.type === type)),
    [categories, type],
  );
  const canExport = includeRecords || (format !== "csv" && (includeCategories || includeAccounts || includeCurrencies));
  const activeAccounts = accounts.filter((account) => !account.is_archived);

  useEffect(() => {
    if (format !== "csv") return;
    setIncludeRecords(true);
    setIncludeCategories(false);
    setIncludeAccounts(false);
    setIncludeCurrencies(false);
  }, [format]);

  useEffect(() => {
    if (categoryId && !availableCategories.some((category) => category.id === categoryId)) {
      setCategoryId("");
    }
  }, [availableCategories, categoryId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canExport) return;
    setError("");
    try {
      const { blob, filename } = await financeApi.exportRecords(group.id, {
        format,
        includeRecords,
        includeCategories,
        includeAccounts,
        includeCurrencies,
        type,
        categoryId,
        userId,
        accountId,
        dateFrom,
        dateTo,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Export failed");
    }
  };

  return (
    <Dialog open onClose={onClose} aria-labelledby="exportRecordsTitle" slotProps={{ paper: { className: "modal records-modal" } }}>
      <DialogContent className="records-modal-content">
        <div className="records-modal-toolbar">
          <h2 id="exportRecordsTitle">Export</h2>
          <IconButton className="records-icon-button" type="button" aria-label="Close export dialog" title="Close" onClick={onClose}>
            <CloseIcon aria-hidden="true" />
          </IconButton>
        </div>
        <form className="records-form" noValidate onSubmit={submit}>
          {error ? <p className="records-error">{error}</p> : null}

          <div className="export-filter-grid">
            <TextField
              select
              size="small"
              label="Type"
              value={type}
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (value === "expense") return "Expense";
                    if (value === "income") return "Income";
                    if (value === "transfer") return "Transfer";
                    if (value === "correction") return "Correction";
                    return "All types";
                  },
                },
              }}
              onChange={(event) => {
                const nextType = event.target.value as TransactionType | "";
                setType(nextType);
                const selectedCategory = categories.find((category) => category.id === categoryId);
                if (selectedCategory && nextType && selectedCategory.type !== nextType) setCategoryId("");
              }}
            >
              <MenuItem value="">All types</MenuItem>
              <MenuItem value="expense">Expense</MenuItem>
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="transfer">Transfer</MenuItem>
              <MenuItem value="correction">Correction</MenuItem>
            </TextField>
            <div className="records-field">
              <CategorySearchSelect
                size="small"
                label="Category"
                value={categoryId}
                emptyLabel="All categories"
                categories={availableCategories}
                allCategories={categories}
                disabled={type === "transfer" || type === "correction"}
                onChange={setCategoryId}
              />
            </div>
            <div className="records-field">
              <UserSelect
                size="small"
                label="Person"
                members={members}
                emptyLabel="All people"
                value={userId}
                onChange={setUserId}
              />
            </div>
            <div className="records-field">
              <AccountSelect
                size="small"
                label="Account"
                accounts={activeAccounts}
                members={members}
                emptyLabel="All accounts"
                value={accountId}
                onChange={setAccountId}
              />
            </div>
            <DatePickerField size="small" label="From" value={dateFrom} onChange={setDateFrom} />
            <DatePickerField size="small" label="To" value={dateTo} onChange={setDateTo} />
          </div>

          <TextField select className="export-format-field" label="Format" value={format} onChange={(event) => setFormat(event.target.value as ExportFormat)} size="small">
            <MenuItem value="xls">XLS</MenuItem>
            <MenuItem value="csv">CSV</MenuItem>
          </TextField>

          <div className="export-toggle-list">
            <FormControlLabel
              control={<Switch checked={includeRecords} onChange={(event) => setIncludeRecords(event.target.checked)} disabled={format === "csv"} />}
              label="Records"
            />
            <FormControlLabel
              control={<Switch checked={includeCategories} onChange={(event) => setIncludeCategories(event.target.checked)} disabled={format === "csv"} />}
              label="Categories"
            />
            <FormControlLabel
              control={<Switch checked={includeAccounts} onChange={(event) => setIncludeAccounts(event.target.checked)} disabled={format === "csv"} />}
              label="Bank accounts"
            />
            <FormControlLabel
              control={<Switch checked={includeCurrencies} onChange={(event) => setIncludeCurrencies(event.target.checked)} disabled={format === "csv"} />}
              label="Currencies"
            />
          </div>

          <div className="records-modal-actions">
            <Button variant="text" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="contained" type="submit" disabled={!canExport} startIcon={<DownloadOutlinedIcon fontSize="small" />}>
              Export
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
